import type * as React from "react";

import type { ConnectionSummary } from "@ando/core";
import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";

import { DiscoveryDialog, EditConnectorDialog } from "@/features/connectors/components";

/**
 * DB / ERP post-create modals — the two additive dialog mounts the route
 * renders alongside the detail SECTION (Jul 2026, post-spreadsheet-section):
 *
 *   1. Discovery dialog — launched from the detail section header.
 *   2. Edit-connector dialog — launched from the detail section header.
 *
 * Schema / Context / Examples all live inside ``<ConnectorDetailSection>`` now
 * (its manifest-driven tabs), for DB/ERP AND spreadsheet alike — so the old
 * inline ``DbErpSchemaPanel`` was removed. The route owns every state slice
 * (``discoveringConnId``, ``editingConnId``) and fans the props to each child.
 */

export interface DbErpManagementPanelsProps {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  /** The connection whose discovery dialog is currently open (null if none). */
  readonly discoveringConnection: ConnectionSummary | null;
  /** The connection whose edit dialog is currently open (null if none). */
  readonly editingConnection: ConnectionSummary | null;
  /** Pre-populated form values for ``EditConnectorDialog``. */
  readonly editingInitialFields: Readonly<Record<string, string>>;
  // ── Handlers (all owned by the route) ──
  readonly onCloseDiscovery: () => void;
  readonly onCloseEdit: () => void;
}

/**
 * Wraps the two conditional modal mounts the route would otherwise inline:
 *
 *   • ``<DiscoveryDialog>``       — gated on ``discoveringConnection``
 *   • ``<EditConnectorDialog>``   — gated on ``editingConnection``
 *
 * Both are launched from the DB/ERP detail section's header actions.
 */
export const DbErpManagementPanels: React.FC<DbErpManagementPanelsProps> = ({
  spec,
  discoveringConnection,
  editingConnection,
  editingInitialFields,
  onCloseDiscovery,
  onCloseEdit,
}) => (
  <>
    {discoveringConnection !== null ? (
      <DiscoveryDialog
        open={discoveringConnection !== null}
        onOpenChange={(o) => {
          if (!o) onCloseDiscovery();
        }}
        connId={discoveringConnection.conn_id}
        onOpenSchema={onCloseDiscovery}
      />
    ) : null}

    {editingConnection !== null ? (
      <EditConnectorDialog
        open={editingConnection !== null}
        onOpenChange={(o) => {
          if (!o) onCloseEdit();
        }}
        connection={editingConnection}
        spec={spec}
        initialFields={editingInitialFields}
      />
    ) : null}
  </>
);
DbErpManagementPanels.displayName = "DbErpManagementPanels";
